/**
 * RehearsalStage — centralized copy strings (AOF 25, i18n posture).
 */

export const rehearsalStageCopy = {
  /**
   * Bands, not columns (animachord#55).
   *
   * The stage stopped being a three-column grid when the take log became
   * the horizontal timeline `DESIGN_SPEC.md` §4.3 specifies, and these
   * labels are what a screen reader reads out to say where it is. Naming
   * them "column" after the columns were gone is the kind of drift that
   * makes the next reader trust the wrong thing.
   */
  scoreBandLabel: "score",
  cueBandLabel: "cues",
  logBandLabel: "take log",
  /**
   * What the closed score band offers.
   *
   * "open the voice lines" rather than "expand" or "show more": the thing
   * behind the disclosure is the score's voice lines, and naming the
   * content is what lets a reader decide whether they want it. A verb
   * naming the widget describes the chrome instead.
   */
  scoreDisclosureLabel: "open the voice lines",
  sendCueLabel: "send cue",
  sendCueDefaultText: "test cue",
  noteInputLabel: "your line",
  noteInputPlaceholder: "type a line…",
  sendNoteLabel: "send",
  status: {
    connecting: "connecting…",
    open: "live",
    closing: "closing…",
    closed: "closed",
    reconnecting: "reconnecting…",
  },
  /**
   * Banner copy, keyed by why the banner is up (#139).
   *
   * This was one fixed title and body behind a boolean, so a visitor who
   * had used up the demo's credits was told "Stage unreachable — check
   * your connection", which blames their network for a server-side
   * ceiling and offers a retry that fails the same way. A boolean cannot
   * carry a reason; that is the whole of the bug.
   *
   * `retryable` is what separates the two families. A socket that did not
   * open may open on the next try. A ceiling will not move because
   * someone pressed a button, and offering the button says it might.
   *
   * These are close to, and deliberately not identical to, the strings in
   * `src/voice/costCap.ts`. That module is the single source for the
   * *voice fallback* copy, which appears in a different surface with no
   * retry affordance and less room. The shared constraint is what
   * `tests/rehearsal/banner-reason.test.ts` asserts and it is the one
   * that matters: a ceiling is never described as a connection problem.
   */
  banner: {
    unreachable: {
      title: "Stage unreachable",
      body: "The rehearsal socket couldn't open. Check your connection and try again.",
      retryable: true,
    },
    "session-credits": {
      title: "Session ended",
      body: "You've used the free credits for this demo session. Start a new one to keep going.",
      retryable: true,
    },
    "demo-at-capacity": {
      title: "The demo is busy",
      body: "The demo is busy right now. It frees up after midnight UTC.",
      retryable: false,
    },
    "voice-limit": {
      title: "Voice limit reached",
      body: "Voice usage limit reached for this billing period.",
      retryable: false,
    },
    retryLabel: "retry",
  },
  loading: "starting rehearsal…",
  defaultScoreName: "Untitled",
  defaultScoreDescription: "Anonymous rehearsal session.",
} as const;
